import React, { ReactNode } from "react";
import { clientAxiosInstance } from "../../lib/types/swrFetcher";

interface ErrorBoundaryProps {
  children: ReactNode;
  session: any;
}

interface ErrorBoundaryState {
  hasError: boolean;
}
const previousErrors: string[] = [];

async function sendError<T = any>(
  error: Error,
  params?: T
): Promise<void | undefined> {
  try {
    if (error.stack) {
      const trimmedStack = error.stack.split("\n").slice(0, 12).join("\n");
      error.stack = trimmedStack;
    }

    const content = JSON.stringify({
      error: error,
      stack: error.stack,
      location: window?.location?.href,
      agent: window?.navigator?.userAgent,
      data: params,
    });

    // No duplicate
    if (previousErrors.includes(content)) return;
    previousErrors.push(content);

    await clientAxiosInstance().post("/api/track", JSON.parse(content));
  } catch (e) {
    console.warn("Failed to send error", e);
  }
}

/**
 * @see https://nextjs.org/docs/pages/building-your-application/configuring/error-handling#handling-client-errors
 */
export default class SlackError extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    if (typeof window !== "undefined") {
      const originalConsoleError = console.error;

      // Override console.error
      console.error = function (message?: any, ...optionalParams: any[]) {
        originalConsoleError(message, ...optionalParams);

        (async () => {
          try {
            await sendError(
              message instanceof Error
                ? message
                : new Error(message?.toString() || "Unknown console error")
            );
          } catch (err) {
            originalConsoleError("Failed to send error to API:", err);
          }
        })();
      };

      // Override window.onerror for uncaught exceptions
      window.onerror = async function (message) {
        (async () => {
          try {
            await sendError(
              message instanceof Error
                ? message
                : new Error(message?.toString() || "Unknown console error")
            );
          } catch (err) {
            console.error("Failed to send error to API:", err);
          }
        })();
      };
    }
  }

  render(): ReactNode {
    return this.props.children;
  }
}
